import React, { CSSProperties, useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import { Col, Layout, Modal, notification, Row, theme } from "antd";
import { Content, Header } from "antd/es/layout/layout";
import { MenuOutlined, CloseOutlined } from "@ant-design/icons";
import { useMobile } from "../hooks/useMobile";
import { ContentWidthProvider } from "./ContainerWithProvider";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { userThunks, userActions } from "user/presentation/store/userSlice";
import { typography } from "theme/Typography";
import { color } from "theme/Color";
import Loader from "../components/Loader";
import AButton from "../components/Button";
import { fleetThunks } from "fleet/presentation/store/fleetSlice";
import { orderThunks } from "order/presentation/store/orderSlice";
import { notificationThunks } from "notifications/presentation/store/notificationSlice";
import AModal from "../components/Modal";
import { WarningTriangleIcon } from "../../../assets/icons/WarningTriangleIconSvg";
import AText from "../components/Text";
import ATitle from "../components/Title";
import { useNavigate } from "react-router-dom";
import { FailedIcon } from "../../../assets/icons/Failed";
import { getCurrentUserDetails } from "../utils/Helper";

const MainLayout = ({ children, renderSidebar }: any): JSX.Element => {
  const isMobile = useMobile();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { showHeader, userProfile } = useAppSelector((state) => state.user);
  const { shipperBids, orders, isLoading, error, digitalContract } =
    useAppSelector((state) => state.order);

  const [collapsed, setCollapsed] = useState(!isMobile);
  const [contractModal, setContractModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showCancelledWarning, setShowCancelledWarning] = useState(true);
  const [accountApprovalModal, setAccountApprovalModal] =
    useState<boolean>(false);
  const [hasCheckedApproval, setHasCheckedApproval] = useState(false);

  // const bidRequestId = 10;
  useEffect(() => {
    dispatch(notificationThunks.getNotificationThunk({ page: 1 }));
  }, []);

  useEffect(() => {
    const toggleHeader = async (shouldShow: boolean) => {
      await dispatch(userThunks.showHeader(shouldShow));
    };

    const hasDigitalContract = userProfile?.user?.digitalContract?.length > 0;

    toggleHeader(!!hasDigitalContract);
  }, [dispatch, userProfile]);

  const handleHeader = async (value: boolean) => {
    await dispatch(userThunks.showHeader(value));
  };
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const contentCSS: CSSProperties = {
    transition: "ease-in-out",
    transitionDuration: "0.2s",
    display: "flex",
    flex: 1,
    minHeight: "100vh",
    overflowY: "auto",
    overflowX: "hidden",
  };

  const handleClick = async () => {
    await dispatch(
      orderThunks.getDigitalContractWithBidRequest(
        userProfile?.user?.digitalContract[0]?.bidRequestId
      )
    );

    setContractModal(true);
  };

  const handleAcknowledge = async () => {
    setLoading(true);

    const data: any = {
      bidRequestId: userProfile?.user?.digitalContract[0]?.bidRequestId,
      contract: digitalContract?.template,
    };

    const { payload } = await dispatch(
      fleetThunks.acknowledgeContractThunk(
        userProfile?.user?.digitalContract[0]?.bidRequestId
      )
    );

    if (payload?.data?.success) {
      setLoading(false);
      setContractModal(false);
      handleHeader(false);
      notification.success({
        message: "Success",
        description: "Contract Acknowledged Successfully!",
      });
    } else {
      setLoading(false);

      notification.error({
        message: "Error",
        description: "Failed to Acknowledge Contract , Please try again..",
      });
    }
  };

  // Warning message logic for cancelled orders
  const cancelledOrders = userProfile?.user?.cancelledOrders ?? 0;

  // Add logout handler
  const handleLogout = () => {
    const userData = {
      id: userProfile?.user?.userId,
      isLogin: false,
    };
    dispatch(userThunks.userLogout(userData));
    navigate("/");
  };

  const handleCloseWarning = () => {
    setShowCancelledWarning(false);
    localStorage.setItem("cancelledOrdersWarningClosed", "true");
    localStorage.setItem("cancelledOrdersCount", String(cancelledOrders));
  };

  useEffect(() => {
    const lastCount = Number(
      localStorage.getItem("cancelledOrdersCount") ?? -1
    );
    const closed =
      localStorage.getItem("cancelledOrdersWarningClosed") === "true";
    // If backend and localStorage counts don't match, reset everything and show warning
    if (cancelledOrders !== lastCount) {
      setShowCancelledWarning(true);
      // localStorage.setItem("cancelledOrdersWarningClosed", "false");
      // Do NOT update cancelledOrdersCount here
    } else if (closed && cancelledOrders === lastCount) {
      setShowCancelledWarning(false);
    } else if (!closed && cancelledOrders === lastCount) {
      setShowCancelledWarning(true);
    }
  }, [cancelledOrders]);

  let cancelledOrderWarning = null;

  // Block logic for modals
  const blockedUntil = userProfile?.user?.blockedUntil;

  const now = new Date();
  const isTemporarilyBlocked =
    cancelledOrders >= 3 &&
    cancelledOrders < 5 &&
    blockedUntil &&
    new Date(blockedUntil) > now;
  const isBlocked = cancelledOrders >= 5;

  // If permanently blocked, logout after 1 minute and show countdown
  const [logoutCountdown, setLogoutCountdown] = useState(10);
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (isBlocked || isTemporarilyBlocked) {
      setLogoutCountdown(10);
      timer = setInterval(() => {
        setLogoutCountdown((prev) => {
          if (prev <= 1) {
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line
  }, [isBlocked, isTemporarilyBlocked]);

  if (showCancelledWarning && cancelledOrders > 0 && cancelledOrders < 3) {
    cancelledOrderWarning = (
      <div
        style={{
          background: "#faf2e3",
          color: "#fca500",
          padding: "8px 0",
          marginBottom: 0,
          borderBottom: `1px solid #f5d9a4`,
          textAlign: "center",
          fontWeight: typography.fontWeights.medium,
          fontSize: typography.fontSizes.xs,
          position: "relative",
          width: "100vw",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1100,
        }}
      >
        <span>
          You have cancelled {cancelledOrders} order
          {cancelledOrders > 1 ? "s" : ""}. Your account will be temporarily
          blocked if you cancel orders 3 times.
        </span>
        <span
          style={{
            position: "absolute",
            right: 24,
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 18,
            color: color.WARNING,
            lineHeight: 1,
            padding: "0 4px",
          }}
          onClick={handleCloseWarning}
          aria-label="Close"
        >
          ×
        </span>
      </div>
    );
  } else if (
    showCancelledWarning &&
    cancelledOrders >= 3 &&
    cancelledOrders < 5 &&
    !isTemporarilyBlocked
  ) {
    cancelledOrderWarning = (
      <div
        style={{
          background: "#FFF1F0",
          color: color.ERROR,
          padding: "8px 0",
          marginBottom: 0,
          borderBottom: `1px solid #FFA39E`,
          textAlign: "center",
          fontWeight: typography.fontWeights.medium,
          fontSize: typography.fontSizes.xs,
          position: "relative",
          width: "100vw",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1100,
        }}
      >
        <span>
          You have cancelled {cancelledOrders} orders. Your account will be
          removed permanently if you cancel orders 5 times.
        </span>
        <span
          style={{
            position: "absolute",
            right: 24,
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 18,
            color: color.ERROR,
            lineHeight: 1,
            padding: "0 4px",
          }}
          onClick={handleCloseWarning}
          aria-label="Close"
        >
          ×
        </span>
      </div>
    );
  } else if (
    showCancelledWarning &&
    cancelledOrders === 3 &&
    !isTemporarilyBlocked
  ) {
    cancelledOrderWarning = (
      <div
        style={{
          background: "#fff1f0",
          color: "#cf1322",
          padding: "10px 0",
          marginBottom: 0,
          borderBottom: `2px solid #ff4d4f`,
          textAlign: "center",
          fontWeight: typography.fontWeights.bold,
          fontSize: typography.fontSizes.md,
          position: "relative",
          width: "100vw",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1100,
          letterSpacing: 0.5,
        }}
      >
        <span>
          <span style={{ fontWeight: 700, color: "#a8071a" }}>Warning: </span>
          You have cancelled 3 orders. Your account is now{" "}
          <span style={{ color: "#cf1322", fontWeight: 700 }}>
            temporarily blocked
          </span>
          . If you cancel 5 orders, your account will be{" "}
          <span style={{ color: "#a8071a", fontWeight: 700 }}>
            removed permanently
          </span>
          .
        </span>
        <span
          style={{
            position: "absolute",
            right: 24,
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 20,
            color: "#cf1322",
            lineHeight: 1,
            padding: "0 4px",
          }}
          onClick={handleCloseWarning}
          aria-label="Close"
        >
          ×
        </span>
      </div>
    );
  } else if (
    showCancelledWarning &&
    cancelledOrders > 3 &&
    cancelledOrders < 5 &&
    !isTemporarilyBlocked
  ) {
    cancelledOrderWarning = (
      <div
        style={{
          background: "#fff1f0",
          color: "#a8071a",
          padding: "10px 0",
          marginBottom: 0,
          borderBottom: `2px solid #ff4d4f`,
          textAlign: "center",
          fontWeight: typography.fontWeights.bold,
          fontSize: typography.fontSizes.md,
          position: "relative",
          width: "100vw",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1100,
          letterSpacing: 0.5,
        }}
      >
        <span>
          <span style={{ fontWeight: 700, color: "#a8071a" }}>Danger: </span>
          You have cancelled {cancelledOrders} orders. Your account will be{" "}
          <span style={{ color: "#cf1322", fontWeight: 700 }}>
            removed permanently
          </span>{" "}
          if you cancel orders 5 times.
        </span>
        <span
          style={{
            position: "absolute",
            right: 24,
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 20,
            color: "#cf1322",
            lineHeight: 1,
            padding: "0 4px",
          }}
          onClick={handleCloseWarning}
          aria-label="Close"
        >
          ×
        </span>
      </div>
    );
  }

  useEffect(() => {
    const checkUserApproval = async () => {
      if (!userProfile || hasCheckedApproval) return;
      const isApproved: boolean = userProfile.user?.isApproved;
      const roleCode = userProfile.user?.roles?.[0]?.roleCode;

      if (!isApproved) {
        const userDetails: any = await getCurrentUserDetails(dispatch);
        const isAdditionalInfoSkipped =
          userDetails?.user?.isAdditionalInfoSkipped;

        if (
          roleCode === "VEHICLE_OWNER" &&
          (userDetails?.userAddress?.streetAddress || isAdditionalInfoSkipped)
        ) {
          setAccountApprovalModal(true);
        } else if (
          roleCode === "SHIPPER" &&
          (userDetails?.user?.address || isAdditionalInfoSkipped)
        ) {
          setAccountApprovalModal(true);
        }
        setHasCheckedApproval(true);
      }
    };

    checkUserApproval();
  }, [userProfile, dispatch, hasCheckedApproval]);

  return (
    <>
      {cancelledOrderWarning}
      {isTemporarilyBlocked && (
        <Modal
          open={true}
          onCancel={() => {}}
          width={600}
          closeIcon={null}
          footer={null}
          centered
        >
          <Row justify="center" style={{ marginBottom: 16 }}>
            <Col>
              <WarningTriangleIcon style={{ fontSize: 120 }} />
            </Col>
          </Row>
          <Row justify="center">
            <Col>
              <ATitle
                style={{
                  color: color.PRIMARY,
                  fontSize: typography.fontSizes.xl,
                  textAlign: "center",
                  marginBottom: 16,
                }}
                content={
                  "Your account has been temporarily blocked due to excessive order cancellations."
                }
              />
            </Col>
          </Row>
          <Row justify="center">
            <Col>
              <AText
                style={{
                  color: color.INPUT_TEXT,
                  fontWeight: 500,
                  fontSize: typography.fontSizes.md,
                  textAlign: "center",
                  maxWidth: 400,
                  marginBottom: 24,
                }}
              >
                Your account will be unblocked on{" "}
                <span style={{ color: color.PRIMARY, fontWeight: 700 }}>
                  {blockedUntil
                    ? (() => {
                        const date = new Date(blockedUntil);
                        const day = date.getDate();
                        const month = date.toLocaleString("default", {
                          month: "long",
                        });
                        const year = date.getFullYear();
                        let hour = date.getHours();
                        const minute = date
                          .getMinutes()
                          .toString()
                          .padStart(2, "0");
                        const second = date
                          .getSeconds()
                          .toString()
                          .padStart(2, "0");
                        const ampm = hour >= 12 ? "PM" : "AM";
                        hour = hour % 12 || 12;
                        return `${day} ${month} ${year} at ${hour}:${minute}:${second}${ampm}`;
                      })()
                    : ""}
                </span>
                .
              </AText>
            </Col>
          </Row>
          <Row justify="center">
            <Col>
              <AText
                style={{
                  color: color.INPUT_TEXT,
                  fontWeight: 500,
                  fontSize: typography.fontSizes.md,
                  textAlign: "center",
                  maxWidth: 400,
                  marginBottom: 24,
                }}
              >
                For any other support, please contact our support team.
              </AText>
            </Col>
          </Row>
          <Row
            justify="center"
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: "16px 20px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              minWidth: 260,
              marginBottom: 16,
            }}
          >
            <Col span={24}>
              <Row gutter={[0, 8]}>
                <Col span={24}>
                  <AText>
                    <strong>Email:</strong>{" "}
                    <a
                      href="mailto:support@lodbod.com"
                      style={{ color: color.PRIMARY }}
                    >
                      support@lodbod.com
                    </a>
                  </AText>
                </Col>
                <Col span={24}>
                  <AText>
                    <strong>Phone:</strong>{" "}
                    <a
                      href="tel:+977-9766388646"
                      style={{ color: color.PRIMARY }}
                    >
                      +977 9766388646
                    </a>
                  </AText>
                </Col>
                <Col span={24}>
                  <AText>
                    <strong>Address:</strong> Bhanimandal -03, Lalitpur, Nepal
                  </AText>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row justify="center" style={{ marginTop: 16 }}>
            <Col>
              <AText
                style={{
                  color: color.ERROR,
                  fontWeight: 700,
                  fontSize: typography.fontSizes.md,
                  textAlign: "center",
                }}
              >
                You will be logged out in {logoutCountdown} second
                {logoutCountdown !== 1 ? "s" : ""}...
                <br />
              </AText>
            </Col>
          </Row>
        </Modal>
      )}
      {isBlocked && (
        <Modal
          open={true}
          onCancel={() => {}}
          width={600}
          closeIcon={null}
          footer={null}
          centered
        >
          <Row justify="center" style={{ marginBottom: 16 }}>
            <Col>
              <WarningTriangleIcon style={{ fontSize: 120 }} />
            </Col>
          </Row>
          <Row justify="center">
            <Col>
              <ATitle
                style={{
                  color: color.PRIMARY,
                  fontSize: typography.fontSizes.xl,
                  textAlign: "center",
                  marginBottom: 16,
                }}
                content={
                  "Your account has been permanently blocked due to excessive order cancellations."
                }
              />
            </Col>
          </Row>
          <Row justify="center">
            <Col>
              <AText
                style={{
                  color: color.INPUT_TEXT,
                  fontWeight: 500,
                  fontSize: typography.fontSizes.md,
                  textAlign: "center",
                  maxWidth: 400,
                  marginBottom: 24,
                }}
              >
                For any other support, please contact our support team.
              </AText>
            </Col>
          </Row>
          <Row
            justify="center"
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: "16px 20px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              minWidth: 260,
              marginBottom: 16,
            }}
          >
            <Col span={24}>
              <Row gutter={[0, 8]}>
                <Col span={24}>
                  <AText>
                    <strong>Email:</strong>{" "}
                    <a
                      href="mailto:support@lodbod.com"
                      style={{ color: color.PRIMARY }}
                    >
                      support@lodbod.com
                    </a>
                  </AText>
                </Col>
                <Col span={24}>
                  <AText>
                    <strong>Phone:</strong>{" "}
                    <a
                      href="tel:+977-9766388646"
                      style={{ color: color.PRIMARY }}
                    >
                      +977 9766388646
                    </a>
                  </AText>
                </Col>
                <Col span={24}>
                  <AText>
                    <strong>Address:</strong> Bhanimandal -03, Lalitpur, Nepal
                  </AText>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row justify="center" style={{ marginTop: 16 }}>
            <Col>
              <AText
                style={{
                  color: color.ERROR,
                  fontWeight: 700,
                  fontSize: typography.fontSizes.md,
                  textAlign: "center",
                }}
              >
                You will be logged out in {logoutCountdown} second
                {logoutCountdown !== 1 ? "s" : ""}...
                <br />
              </AText>
            </Col>
          </Row>
        </Modal>
      )}
      {showHeader && (
        <Header style={headerStyle}>
          <div>
            You have a pending acknowledgment to acknowledge, please agree to
            the contract to complete the order process.{" "}
            <a
              onClick={handleClick}
              style={{ color: color.PRIMARY, fontWeight: 700 }}
            >
              Click here to view Digital Contract.
            </a>
          </div>
        </Header>
      )}
      <Layout style={{ display: "flex", flex: 1 }}>
        {renderSidebar && collapsed && (
          <Sidebar collapsed={!collapsed} setCollapsed={setCollapsed} />
        )}
        <Layout style={{ display: "flex", flex: 1 }}>
          <Content style={{ ...contentCSS }}>
            {renderSidebar && !collapsed && (
              <div style={{ position: "relative" }}>
                <MenuOutlined
                  onClick={() => setCollapsed(true)}
                  style={{
                    position: "absolute",
                    top: "30px",
                    left: "35px",
                    zIndex: 1,
                    fontSize: "20px",
                    cursor: "pointer",
                  }}
                />
              </div>
            )}
            <Content
              id="main-content"
              style={{
                margin: "0 1rem 0rem 1rem",
                display: "flex",
                flex: 1,
                maxWidth: "100%",
              }}
            >
              <ContentWidthProvider>{children}</ContentWidthProvider>
            </Content>
          </Content>
        </Layout>
      </Layout>

      <Modal
        open={contractModal}
        onCancel={() => setContractModal(false)}
        width={"90%"}
        closeIcon={<CloseOutlined />}
        footer={null}
        centered
        style={{ maxHeight: "80vh", maxWidth: "60vw" }} // Set a maximum height for the modal
      >
        <div className="modal-body">
          <p
            style={{
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semibold,
              lineHeight: "28px",
              color: color.TEXT_COLOR,
            }}
          >
            Digital Contract
          </p>
          {digitalContract ? (
            <p
              style={{
                fontSize: typography.fontSizes.sm,
                fontWeight: typography.fontWeights.normal,
                lineHeight: "20px",
                color: color.TEXT,
              }}
              dangerouslySetInnerHTML={{ __html: digitalContract?.template }}
            />
          ) : (
            <Loader />
          )}
        </div>
        <Row gutter={14} style={{ marginTop: "30px" }}>
          <Col
            span={24}
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <AButton
              type="text"
              mode="darkoutlined"
              onClick={() => setContractModal(false)}
            >
              Cancel
            </AButton>
            <AButton
              type="primary"
              onClick={() => {
                handleAcknowledge();
                setContractModal(false);
              }}
            >
              Acknowledge
            </AButton>
          </Col>
        </Row>
      </Modal>
      <Modal
        open={accountApprovalModal}
        width={400}
        closeIcon={null}
        centered
        footer={[
          <div style={{ display: "flex", flexDirection: "row" }}>
            <AButton
              key="continueButton"
              onClick={() => handleLogout()}
              fullwidth
              type="primary"
            >
              Log Out
            </AButton>
          </div>,
        ]}
      >
        <div className="modal-body">
          <FailedIcon />
          <p
            style={{
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semibold,
              lineHeight: "28px",
            }}
          >
            Account Approval Pending
          </p>
          <p
            style={{
              fontSize: typography.fontSizes.sm,
              fontWeight: typography.fontWeights.normal,
              lineHeight: "20px",
            }}
          >
            Your account is under review. Our support representative will
            contact you for verification. The verification process may take some
            time. Thank you for your understanding. For further information,
            please contact 9766388646.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default MainLayout;

const headerStyle: React.CSSProperties = {
  height: "auto",
  padding: "10px 20px",
  backgroundColor: "#ffffff",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  zIndex: 1000,
  lineHeight: 1,
};
